import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { useSocket } from "@/lib/useSocket";
import AnalyticsPanel from "@/components/AnalyticsPanel";
import MapView from "@/components/MapView";
import InstallPrompt from "@/components/InstallPrompt";
import SubscribePaywall from "@/pages/SubscribePaywall";
import { Plus, Phone, User as UserIcon, KeyRound, MapPin, QrCode, Sparkles, Lock, Clock, Crown, CalendarClock, LifeBuoy, Image as ImageIcon } from "lucide-react";

const OPENING_STATUS_CLASSES = {
  open: "bg-[#C9A85F] text-black",
  filled: "bg-[#34D399]/15 text-[#059669]",
};
const OPENING_STATUS_FALLBACK = "bg-muted text-muted-foreground";

import { fmtSydneyDateTime, fmtSydneyDay, fmtSydneyTime } from "@/lib/timezone";
import { displayPrice, priceToNumber } from "@/lib/price";

// Thin local aliases — every Dashboard time render must be Sydney-local so
// businesses and customers see the same wall-clock time regardless of their
// browser's timezone. See `/app/frontend/src/lib/timezone.js` for why.
const fmtTime = fmtSydneyDateTime;
const fmtApptDay = fmtSydneyDay;
const fmtApptTime = fmtSydneyTime;

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [openings, setOpenings] = useState([]);
  const [requests, setRequests] = useState([]);
  const [tab, setTab] = useState("pending");

  const load = useCallback(async () => {
    try {
      const [op, rq] = await Promise.all([
        api.get("/openings/mine"),
        api.get("/requests/mine"),
      ]);
      setOpenings(op.data);
      setRequests(rq.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- api/toast are stable module imports

  useEffect(() => {
    load();
  }, [load]);

  // Real-time updates via WebSocket
  useSocket(
    "business",
    (msg) => {
      if (msg?.type === "request.created" || msg?.type === "inbox.changed") {
        load();
        if (msg.type === "request.created") {
          toast("New booking request just arrived", { icon: "→" });
        }
      }
    },
    !!user,
  );

  const openingById = (id) => openings.find((o) => o.id === id);

  const respond = async (id, action) => {
    try {
      const req = requests.find((x) => x.id === id);
      const wasApproved = req?.status === "approved";
      await api.post(`/requests/${id}/${action}`);
      // Echo the appointment slot on approval so the owner sees confirmation
      // of exactly WHEN the customer is booked in — matches the appointment
      // column on the card and reduces "wait, when was that for again?" doubt.
      if (action === "approve") {
        const o = req ? openingById(req.opening_id) : null;
        const slot = o?.start_time
          ? ` for ${fmtApptDay(o.start_time)} at ${fmtApptTime(o.start_time)}`
          : "";
        toast.success(`Booking approved${slot}`);
      } else if (wasApproved) {
        toast.success("Booking cancelled — the slot is open again");
      } else {
        toast.success("Request declined");
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // One-click Platinum upgrade. Only visible when the user has an active
  // subscription (card on file) and isn't already Platinum. The endpoint
  // uses stripe.Subscription.modify with `always_invoice` proration so the
  // difference is charged immediately against the saved card — no Checkout
  // redirect, no re-entering card details.
  const [upgrading, setUpgrading] = useState(false);
  const upgradeToPlatinum = async () => {
    const ok = window.confirm(
      "Upgrade to Platinum ($99/mo)?\n\nWe'll charge the prorated difference to your saved card and unlock priority map placement, the 'Featured' badge, and monthly performance reports immediately.",
    );
    if (!ok) return;
    setUpgrading(true);
    try {
      await api.post("/payments/upgrade-to-platinum");
      toast.success("You're on Platinum — priority placement is live now");
      await refresh();
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUpgrading(false);
    }
  };

  const filteredRequests = requests.filter((r) => {
    if (tab === "all") return true;
    // A customer-cancelled booking is shown alongside business-declined
    // ones in the Declined tab so the owner has a single "gone" bucket.
    if (tab === "declined") return r.status === "declined" || r.status === "cancelled";
    return r.status === tab;
  });

  const stats = {
    open: openings.filter((o) => o.status === "open").length,
    filled: openings.filter((o) => o.status === "filled").length,
    pending: requests.filter((r) => r.status === "pending").length,
    approved: requests.filter((r) => r.status === "approved").length,
  };

  // Metric-card values — Openings (total posted), Bookings (approved requests
  // — this is the platform's north-star signal), Estimated Revenue (sum of
  // opening prices for every "filled" slot, i.e. bookings that closed).
  const metrics = useMemo(() => {
    const totalOpenings = openings.length;
    const successfulBookings = requests.filter((r) => r.status === "approved").length;
    const estimatedRevenue = openings
      .filter((o) => o.status === "filled")
      .reduce((sum, o) => sum + priceToNumber(o.price), 0);
    return { totalOpenings, successfulBookings, estimatedRevenue };
  }, [openings, requests]);

  const fmtCurrency = (n) =>
    "$" +
    Number(n).toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });

  // Personalised greeting — first name if we have it, otherwise business name.
  const greetingName = (
    user?.business_name ||
    user?.name?.split(" ")[0] ||
    "there"
  );

  // Business address centering + a synthetic pin so the map has something to
  // show even before any openings exist.
  const hasBusinessLocation =
    typeof user?.lat === "number" && typeof user?.lng === "number";

  const mapCenter = useMemo(() => {
    if (hasBusinessLocation) return [user.lat, user.lng];
    if (openings.length > 0) return [openings[0].lat, openings[0].lng];
    return [-33.8688, 151.2093]; // fallback: Sydney CBD
  }, [hasBusinessLocation, user?.lat, user?.lng, openings]);

  const mapOpenings = useMemo(() => {
    if (!hasBusinessLocation) return openings;
    // Add a "home" pin at the business's address so the location is obvious
    // even with zero openings. Distinct id prefix so click handlers know it's
    // not a bookable slot.
    return [
      {
        id: `__home__${user.id}`,
        lat: user.lat,
        lng: user.lng,
        service_type: user.business_name || user.name || "You",
        title: user.business_name || user.name || "Your business",
        status: "home",
      },
      ...openings,
    ];
  }, [openings, hasBusinessLocation, user]);

  // ---- Trial state ---------------------------------------------------------
  // The backend already gates POST /openings with a 402 when expired, so this
  // is purely a UX affordance so a locked-out business knows *why* actions
  // aren't working. Consumers/admin never hit this — `trial_active` is null.
  const trialDaysRemaining = user?.trial_days_remaining ?? null;
  const trialExpired = user?.trial_active === false;
  const trialUrgent = typeof trialDaysRemaining === "number" && trialDaysRemaining <= 7;
  // Active paid subscription — swap the CTA from "View plans" to "Manage subscription".
  const isSubscribed = ["active", "trialing", "past_due"].includes(
    user?.subscription_status,
  );
  // Paid (non-trial) subscription — hides the trial countdown entirely.
  const hasPaidSubscription = ["active", "past_due"].includes(user?.subscription_status);

  if (!isSubscribed && trialExpired) {
    return <SubscribePaywall user={user} />;
  }

  // First-signup onboarding gate — force fresh business signups onto the
  // subscription picker before they can reach the dashboard. Flipped to
  // true when they complete Stripe checkout (payments._mark_paid_and_activate).
  // Legacy accounts got backfilled to true at deploy time so they're unaffected.
  if (user?.role === "business" && user?.plan_selection_completed === false) {
    return <SubscribePaywall user={user} expired={false} />;
  }

  return (
    <div className="mx-auto max-w-[1400px] p-4 md:p-8 lg:p-10">
      {/* Missing-phone prompt — one-time nudge for existing businesses that
          pre-date the required-phone rule. Non-dismissable so it stays until
          they add a number (customers get it after booking is approved). */}
      {!user?.phone && (
        <div
          data-testid="dashboard-missing-phone-banner"
          className="mb-4 md:mb-6 border border-[#C9A85F] bg-[#FDF4D8] p-4 md:p-5 flex flex-col gap-2 md:flex-row md:items-center md:justify-between"
        >
          <div>
            <p className="mono text-[10px] uppercase tracking-[0.25em] text-[#A88A47]">
              action required
            </p>
            <p className="mt-1 text-sm">
              <span className="font-semibold">Add a mobile number</span> so
              approved customers can reach you before the appointment.
            </p>
          </div>
          <Link
            to="/dashboard/profile"
            data-testid="dashboard-add-phone-cta"
            className="inline-flex h-11 items-center gap-2 gold-metallic-bg px-4 text-xs font-semibold uppercase tracking-widest text-[#2A2724] active:scale-[0.98]"
          >
            Add number
          </Link>
        </div>
      )}

      {/* Foundation Member welcome banner — permanent for the trial duration */}
      {user?.is_foundation && trialDaysRemaining !== null && !hasPaidSubscription && (
        <div
          data-testid="dashboard-foundation-banner"
          className="mb-4 md:mb-6 border border-[#C9A85F] bg-gradient-to-r from-[#FDF4D8] via-white to-[#FDF4D8] p-4 md:p-5 flex flex-col gap-2 md:flex-row md:items-center md:justify-between"
        >
          <div className="flex items-start gap-3">
            <div className="mt-0.5 grid h-8 w-8 flex-shrink-0 place-items-center border border-[#C9A85F] gold-metallic-bg">
              <Sparkles size={14} className="text-black" />
            </div>
            <div>
              <p className="mono text-[10px] uppercase tracking-[0.25em] text-[#A88A47]">
                foundation member
              </p>
              <p className="mt-1 text-sm md:text-base">
                <span className="font-semibold">Welcome, Foundation Member!</span>{" "}
                You have 2 months free to get your customers on board.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Days-remaining counter — only for businesses on a trial without a paid subscription */}
      {trialDaysRemaining !== null && !hasPaidSubscription && (
        <div
          data-testid="dashboard-trial-counter"
          className={
            "mb-4 md:mb-6 border p-3 md:p-4 flex items-center justify-between gap-4 " +
            (trialUrgent ? "border-[#C9A85F] bg-[#FDF4D8]" : "border-border")
          }
        >
          <div className="flex items-center gap-3">
            <Clock size={16} className={trialUrgent ? "text-[#A88A47]" : "text-muted-foreground"} />
            <div>
              <p className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                trial
              </p>
              <p className="mt-0.5 text-sm">
                <span
                  data-testid="dashboard-trial-days-remaining"
                  className={
                    "font-semibold tabular-nums " +
                    (trialUrgent ? "text-[#A88A47]" : "")
                  }
                >
                  {trialDaysRemaining} day{trialDaysRemaining === 1 ? "" : "s"}
                </span>{" "}
                remaining
                {user?.is_foundation ? " on your Foundation trial" : ""}
              </p>
            </div>
          </div>
          {trialUrgent && !isSubscribed ? (
            <Link
              to="/dashboard/subscribe"
              data-testid="dashboard-trial-reactivate-cta"
              className="mono text-[10px] uppercase tracking-widest border border-[#A88A47] px-3 py-2 text-[#A88A47] hover:bg-[#A88A47] hover:text-white transition-colors"
            >
              Subscribe now
            </Link>
          ) : isSubscribed ? (
            <Link
              to="/dashboard/subscribe"
              data-testid="dashboard-manage-subscription-cta"
              className="mono text-[10px] uppercase tracking-widest border border-border px-3 py-2 text-muted-foreground hover:border-foreground hover:text-foreground transition-colors"
            >
              Manage subscription
            </Link>
          ) : (
            <Link
              to="/dashboard/subscribe"
              data-testid="dashboard-view-plans-cta"
              className="mono text-[10px] uppercase tracking-widest border border-border px-3 py-2 text-muted-foreground hover:border-[#C9A85F] hover:text-[#8A7333] transition-colors"
            >
              View plans
            </Link>
          )}
        </div>
      )}

      {/* Welcome header — personalised, generous whitespace, hero CTA */}
      <section
        data-testid="dashboard-welcome"
        className="border border-border p-6 md:p-10 lg:p-12"
      >
        <div className="grid gap-8 lg:grid-cols-[1.4fr_1fr] lg:items-end">
          {/* Left column — greeting + supporting line */}
          <div>
            <p className="mono text-[10px] uppercase tracking-[0.35em] text-[#C9A85F]">
              → dashboard
            </p>
            <h1
              data-testid="dashboard-welcome-heading"
              className="mt-4 text-4xl leading-[1.05] tracking-tight md:text-5xl lg:text-6xl"
            >
              <span className="inline-flex items-baseline flex-wrap gap-x-3 gap-y-2">
                <span>Welcome,</span>
                <span className="gold-metallic-text wordmark">{greetingName}</span>
                {user?.is_platinum && (
                  <span
                    data-testid="dashboard-platinum-crown"
                    aria-label="Platinum business"
                    title="Platinum business"
                    className="inline-flex items-center justify-center gold-metallic-bg h-7 w-7 md:h-9 md:w-9 self-center"
                  >
                    <Crown size={16} className="text-[#2A2724]" strokeWidth={2.5} />
                  </span>
                )}
                <span>.</span>
              </span>
            </h1>
            <p className="slogan mt-6 text-2xl leading-snug text-muted-foreground md:text-3xl">
              Turn your empty appointment slots into revenue.
            </p>
          </div>

          {/* Right column — hero-scale Post Opening CTA + secondary links */}
          <div className="flex flex-col gap-3">
            <Link
              to="/dashboard/new"
              data-testid="new-opening-btn"
              className="group inline-flex h-16 items-center justify-center gap-3 gold-metallic-bg px-6 text-base font-semibold uppercase tracking-[0.15em] text-black focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2 active:scale-[0.99] shadow-[0_4px_20px_-4px_rgba(212,175,55,0.35)]"
            >
              <Plus size={22} strokeWidth={2.5} />
              Post an Opening
            </Link>
            <p className="mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground text-center lg:text-right">
              → fill a last-minute slot in under 60 seconds
            </p>
            {/* One-click Platinum upgrade — only surfaced for subscribed
                non-Platinum users. The button hits `/payments/upgrade-to-platinum`
                which mutates the existing Stripe sub instead of opening a
                Checkout redirect, since the card is already on file. */}
            {user?.subscription_status &&
              user.subscription_status !== "canceled" &&
              !user?.is_platinum && (
                <button
                  type="button"
                  onClick={upgradeToPlatinum}
                  disabled={upgrading}
                  data-testid="dashboard-upgrade-platinum-btn"
                  className="mt-1 group inline-flex h-11 items-center justify-center gap-2 border border-[#C9A85F] px-4 text-[11px] font-semibold uppercase tracking-[0.25em] text-[#C9A85F] transition-colors hover:bg-[#C9A85F] hover:text-black disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Crown size={14} strokeWidth={2.5} />
                  {upgrading ? "Upgrading…" : "Upgrade to Platinum · $99/mo"}
                </button>
              )}
            <div className="mt-2 flex flex-wrap justify-center lg:justify-end gap-2">
              <Link
                to="/dashboard/integrations"
                data-testid="integrations-link"
                className="inline-flex h-10 items-center gap-2 border border-border px-3 text-[11px] uppercase tracking-widest hover:border-foreground transition-colors"
              >
                <KeyRound size={12} /> API & Webhooks
              </Link>
              <Link
                to="/dashboard/marketing"
                data-testid="marketing-link"
                className="inline-flex h-10 items-center gap-2 border border-border px-3 text-[11px] uppercase tracking-widest hover:border-foreground transition-colors"
              >
                <QrCode size={12} /> Marketing Kit
              </Link>
              {/* Support — mailto opens the user's default mail client with a
                  pre-filled subject line + context (business name, dashboard
                  URL) so we can triage tickets faster without asking the user
                  to fish for account info. */}
              <a
                href={`mailto:support@bookaway.com.au?subject=${encodeURIComponent(
                  `Book Away support — ${user?.business_name || user?.email || "business"}`,
                )}&body=${encodeURIComponent(
                  "Hi Book Away team,\n\n" +
                    "I need help with:\n(describe the issue in a few lines)\n\n" +
                    "---\nAccount: " + (user?.email || "") + "\n" +
                    "Business: " + (user?.business_name || "") + "\n" +
                    "Page: " + (typeof window !== "undefined" ? window.location.href : "") +
                    "\nSent from the business dashboard",
                )}`}
                data-testid="support-link"
                className="inline-flex h-10 items-center gap-2 border border-[#C9A85F] px-3 text-[11px] uppercase tracking-widest text-[#8A7333] hover:bg-[#C9A85F]/10 transition-colors"
              >
                <LifeBuoy size={12} /> Support
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 3 Metric cards — Openings / Bookings / Revenue */}
      <section
        data-testid="dashboard-metrics"
        className="mt-4 md:mt-6 grid grid-cols-1 gap-4 md:gap-6 md:grid-cols-3"
      >
        {[
          {
            key: "openings",
            label: "Openings posted",
            value: metrics.totalOpenings.toString().padStart(2, "0"),
            sub: `${stats.open} live · ${stats.filled} filled`,
          },
          {
            key: "bookings",
            label: "Successful bookings",
            value: metrics.successfulBookings.toString().padStart(2, "0"),
            sub: stats.pending
              ? `${stats.pending} pending review`
              : "All caught up",
          },
          {
            key: "revenue",
            label: "Estimated revenue",
            value: fmtCurrency(metrics.estimatedRevenue),
            sub: "from filled slots to date",
          },
        ].map((m) => (
          <div
            key={m.key}
            data-testid={`metric-card-${m.key}`}
            className="hover-lift border border-border p-6 md:p-8"
          >
            <p className="mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
              {m.label}
            </p>
            <p
              data-testid={`metric-${m.key}-value`}
              className="mono mt-4 text-5xl font-medium tracking-tight md:text-6xl"
            >
              {m.value}
            </p>
            <p className="mono mt-3 text-[10px] uppercase tracking-[0.2em] text-[#C9A85F]">
              → {m.sub}
            </p>
          </div>
        ))}
      </section>

      {/* Install-as-app prompt (Chrome/Edge on Android + desktop, iOS Safari
          hint on iPhone/iPad). Renders only when installable and un-dismissed. */}
      <InstallPrompt />

      {/* Address banner (business without a saved address yet) */}
      {!hasBusinessLocation && (
        <div
          data-testid="dashboard-address-banner"
          className="mt-6 border border-[#C9A85F] bg-[#C9A85F]/5 p-4 md:p-5 flex flex-col gap-2 md:flex-row md:items-center md:justify-between"
        >
          <div className="flex items-start gap-3">
            <MapPin size={18} className="text-[#C9A85F] mt-0.5 shrink-0" />
            <div>
              <p className="mono text-[10px] uppercase tracking-[0.3em] text-[#C9A85F]">
                Location needed
              </p>
              <p className="mt-1 text-sm">
                Add your business address so the dashboard map centers on you
                and consumers can find your openings.
              </p>
            </div>
          </div>
          <Link
            to="/dashboard/profile"
            data-testid="set-address-link"
            className="inline-flex shrink-0 items-center justify-center gap-2 gold-metallic-bg px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-black transition-colors"
          >
            Set business address
          </Link>
        </div>
      )}

      {/* Business location map */}
      <div className="mt-8 border border-border">
        <div className="flex items-center justify-between border-b border-border p-4 md:px-6">
          <div>
            <h2 className="text-xl tracking-tight">Your Map</h2>
            <p className="mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground mt-0.5">
              {hasBusinessLocation
                ? user.address || "Business location"
                : "Set an address to center the map"}
            </p>
          </div>
          <Link
            to="/dashboard/profile"
            data-testid="edit-address-link"
            className="mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground border border-border px-3 py-1.5"
          >
            Edit address
          </Link>
        </div>
        <div
          data-testid="dashboard-map"
          className="h-[320px] md:h-[400px] w-full"
        >
          <MapView
            openings={mapOpenings}
            center={mapCenter}
          />
        </div>
      </div>

      {/* Business profile preview card — cover photo, bio, categories.
          Full edit lives on /dashboard/profile; this card is here so the
          business sees their public-facing profile at a glance from the
          dashboard home. */}
      <div
        data-testid="dashboard-business-profile-card"
        className="mt-8 border-2 border-[#C9A85F]/60 bg-[#FBF8F2] p-2 shadow-[0_16px_40px_-24px_rgba(42,39,36,0.35)] md:p-3"
      >
        <div className="flex items-center justify-between gap-4 border-b border-[#C9A85F]/40 p-4 md:px-6">
          <div>
            <h2 className="text-xl tracking-tight">Business Profile</h2>
            <p className="mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground mt-0.5">
              How customers see you in the Book Away app
            </p>
          </div>
          <Link
            to="/dashboard/profile"
            data-testid="edit-business-profile-link"
            className="gold-metallic-bg shrink-0 px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-[#2A2724] shadow-[0_8px_20px_-10px_rgba(42,39,36,0.55)] transition-transform hover:-translate-y-0.5"
          >
            Edit profile
          </Link>
        </div>
        <div className="p-4 md:p-6 grid gap-4 md:grid-cols-[200px_1fr] md:gap-6">
          {user?.cover_photo_id ? (
            <img
              src={`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/files/${user.cover_photo_id}`}
              alt="Cover"
              data-testid="dashboard-profile-cover"
              className="w-full h-32 md:h-full md:max-h-[160px] object-cover border border-border"
            />
          ) : (
            <Link
              to="/dashboard/profile"
              data-testid="dashboard-profile-cover-empty"
              className="flex h-32 w-full flex-col items-center justify-center gap-2 border border-dashed border-border bg-muted/20 text-muted-foreground hover:border-[#C9A85F] hover:text-[#8A7333] transition-colors md:h-full md:max-h-[160px]"
            >
              <ImageIcon size={20} className="text-[#C9A85F]" />
              <span className="mono text-[10px] uppercase tracking-widest">
                Add cover photo
              </span>
            </Link>
          )}
          <div className="flex flex-col gap-3">
            <div>
              <p className="text-sm font-medium">
                {user?.business_name || user?.name || "Your business"}
              </p>
              {user?.service_categories?.length > 0 ? (
                <div
                  data-testid="dashboard-profile-categories"
                  className="mt-2 flex flex-wrap gap-1.5"
                >
                  {user.service_categories.map((c) => (
                    <span
                      key={c}
                      className="mono text-[10px] uppercase tracking-widest border border-border px-2 py-0.5 text-muted-foreground"
                    >
                      {c}
                    </span>
                  ))}
                </div>
              ) : (
                <Link
                  to="/dashboard/profile"
                  data-testid="dashboard-profile-categories-empty"
                  className="mono text-[10px] uppercase tracking-widest text-[#8A7333] hover:underline mt-2 inline-block"
                >
                  → Pick your service categories
                </Link>
              )}
            </div>
            {user?.bio ? (
              <p
                data-testid="dashboard-profile-bio"
                className="text-sm text-muted-foreground leading-relaxed line-clamp-4"
              >
                {user.bio}
              </p>
            ) : (
              <Link
                to="/dashboard/profile"
                data-testid="dashboard-profile-bio-empty"
                className="mono text-[10px] uppercase tracking-widest text-[#8A7333] hover:underline"
              >
                → Add a short bio
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Analytics */}
      <AnalyticsPanel />

      {/* Requests section */}
      <div className="mt-8 border border-border">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between border-b border-border p-4 md:px-6">
          <h2 className="text-xl tracking-tight">Booking Requests</h2>
          <div className="flex border border-border overflow-x-auto">
            {["pending", "approved", "declined", "all"].map((t) => (
              <button
                key={t}
                data-testid={`requests-tab-${t}`}
                onClick={() => setTab(t)}
                className={`h-10 min-w-[72px] shrink-0 px-3 text-[10px] uppercase tracking-widest transition-colors ${
                  tab === t ? "bg-foreground text-background" : "hover:bg-muted"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        {filteredRequests.length === 0 ? (
          <div className="p-8 mono text-xs text-muted-foreground">
            No {tab === "all" ? "" : tab} requests.
          </div>
        ) : (
          <ul>
            {filteredRequests.map((r) => {
              const o = openingById(r.opening_id);
              return (
                <li
                  key={r.id}
                  data-testid={`request-row-${r.id}`}
                  className="grid grid-cols-1 gap-3 border-b border-border p-4 md:grid-cols-[1.1fr_1fr_1fr_auto] md:items-center md:gap-5 md:px-6"
                >
                  <div>
                    <p className="mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                      {o?.service_type || "—"} · Requested {fmtTime(r.created_at)}
                    </p>
                    <p className="mt-1 text-base leading-tight">
                      {o?.title || "Opening"}
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {o?.address}
                    </p>
                  </div>

                  {/* Appointment slot — the WHEN the customer is coming in.
                      Rendered on every card (pending, approved, declined) so
                      the business owner can eyeball the schedule without
                      opening the parent Opening. */}
                  <div
                    data-testid={`request-appt-${r.id}`}
                    className="rounded-sm border-l-2 border-[#C9A85F] bg-[#C9A85F]/5 px-3 py-2"
                  >
                    <p className="mono flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-[#8A7333]">
                      <CalendarClock size={11} /> Appointment
                    </p>
                    {o?.start_time ? (
                      <>
                        <p
                          data-testid={`request-appt-day-${r.id}`}
                          className="mt-1 text-sm font-medium leading-tight"
                        >
                          {fmtApptDay(o.start_time)}
                        </p>
                        <p
                          data-testid={`request-appt-time-${r.id}`}
                          className="mono text-xs text-muted-foreground"
                        >
                          {fmtApptTime(o.start_time)}
                          {o.end_time ? ` – ${fmtApptTime(o.end_time)}` : ""}
                        </p>
                      </>
                    ) : (
                      <p className="mt-1 text-xs text-muted-foreground">—</p>
                    )}
                  </div>

                  <div className="grid gap-1 text-sm">
                    <span className="inline-flex items-center gap-1.5">
                      <UserIcon size={12} className="text-muted-foreground" />
                      {r.name || "—"}
                    </span>
                    <span className="mono inline-flex items-center gap-1.5">
                      <Phone size={12} className="text-muted-foreground" />
                      {r.phone}
                    </span>
                    {r.note && (
                      <span className="mt-1 border-l-2 border-[#C9A85F] pl-2 text-xs text-muted-foreground">
                        “{r.note}”
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 md:justify-end">
                    {r.status === "pending" && (
                      <>
                        <button
                          data-testid={`decline-btn-${r.id}`}
                          onClick={() => respond(r.id, "decline")}
                          className="flex-1 md:flex-none h-11 md:h-auto border border-border px-4 md:py-1.5 text-[11px] uppercase tracking-widest hover:border-foreground hover:bg-foreground hover:text-background transition-colors"
                        >
                          Decline
                        </button>
                        <button
                          data-testid={`approve-btn-${r.id}`}
                          onClick={() => respond(r.id, "approve")}
                          className="flex-1 md:flex-none h-11 md:h-auto gold-metallic-bg px-4 md:py-1.5 text-[11px] font-semibold uppercase tracking-widest text-black active:scale-[0.98] transition-transform"
                        >
                          Approve
                        </button>
                      </>
                    )}
                    {r.status === "approved" && (
                      <div className="flex items-center gap-2">
                        <span
                          data-testid={`request-status-${r.id}`}
                          className="mono px-2 py-1 text-[10px] uppercase tracking-widest bg-[#34D399]/15 text-[#059669]"
                        >
                          approved
                        </span>
                        <button
                          data-testid={`cancel-btn-${r.id}`}
                          onClick={() => {
                            const ok = window.confirm(
                              "Cancel this confirmed booking? The customer will be emailed, and the slot will re-open for other customers.",
                            );
                            if (ok) respond(r.id, "decline");
                          }}
                          className="h-9 border border-[#F87171]/60 px-3 text-[10px] uppercase tracking-widest text-[#B91C1C] hover:bg-[#F87171]/10 transition-colors"
                        >
                          Cancel booking
                        </button>
                      </div>
                    )}
                    {r.status === "declined" && (
                      <span
                        data-testid={`request-status-${r.id}`}
                        className="mono px-2 py-1 text-[10px] uppercase tracking-widest bg-muted text-muted-foreground"
                      >
                        declined
                      </span>
                    )}
                    {r.status === "cancelled" && (
                      <span
                        data-testid={`request-status-${r.id}`}
                        className="mono px-2 py-1 text-[10px] uppercase tracking-widest bg-muted text-muted-foreground"
                      >
                        cancelled by customer
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Openings section */}
      <div className="mt-8 border border-border">
        <div className="border-b border-border p-4 md:px-6">
          <h2 className="text-xl tracking-tight">Your Openings</h2>
        </div>
        {openings.length === 0 ? (
          <div className="p-8 mono text-xs text-muted-foreground">
            You haven't posted any openings yet.
          </div>
        ) : (
          <ul>
            {openings.map((o) => (
              <li
                key={o.id}
                data-testid={`my-opening-${o.id}`}
                className="grid grid-cols-1 gap-3 border-b border-border p-4 md:grid-cols-[1fr_1fr_auto] md:items-center md:gap-6 md:px-6"
              >
                <div>
                  <p className="mono text-[10px] uppercase tracking-[0.25em] text-[#C9A85F]">
                    {o.service_type}
                  </p>
                  <p className="mt-1 text-base leading-tight">{o.title}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {o.address}
                  </p>
                </div>
                <div className="mono text-xs text-muted-foreground">
                  <p>{fmtTime(o.start_time)}</p>
                  <p>→ {fmtTime(o.end_time)}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="mono text-sm">
                    {displayPrice(o.price)}
                  </span>
                  <span
                    className={`mono px-2 py-1 text-[10px] uppercase tracking-widest ${
                      OPENING_STATUS_CLASSES[o.status] || OPENING_STATUS_FALLBACK
                    }`}
                  >
                    {o.status}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
